import { useState, useMemo } from "react";
import { priceGroupMedical, summarizeCategory } from "@fa/pricing";
import { groupMedicalRates } from "@fa/rates";
import {
  groupMedicalPayloadSchema,
  todayIso,
  type JobCategory,
  type AgeBand,
} from "@fa/shared";
import { Field, FormError, QuoteTable, RatesDisclaimer, ReviewSummary, StepIndicator, StepNavigation, SubmitButton } from "../components/Shared";
import { useFormValidation } from "../hooks/useFormValidation";
import { useStepWizard, type StepDef } from "../hooks/useStepWizard";
import { submit, useApiBase } from "../api";

const CAT_IDS = ["A", "B", "C", "D", "E", "F"] as const;

const POP_LABELS: Record<string, string> = {
  M: "Member Only",
  Mplus1: "M + 1",
  Mplus2: "M + 2",
  Mplus3: "M + 3",
  Mplus4: "M + 4",
  Mplus5: "M + 5",
  additionalDependents: "Extra Dependents",
};

function emptyCategory(id: (typeof CAT_IDS)[number]): JobCategory {
  return {
    id,
    population: {
      M: 0,
      Mplus1: 0,
      Mplus2: 0,
      Mplus3: 0,
      Mplus4: 0,
      Mplus5: 0,
      additionalDependents: 0,
    },
    sharing: {
      inpatient: "SHARED",
      outpatient: "SHARED",
      dental: "PER_PERSON",
      optical: "PER_PERSON",
      lastExpense: "PER_PERSON",
      maternity: "PER_FAMILY",
      personalAccident: "PER_PERSON",
    },
    limits: {
      inpatientKes: 1000000,
      outpatientKes: 100000,
      dentalKes: 15000,
      opticalKes: 15000,
      maternityKes: 150000,
      lastExpenseKes: 100000,
      criticalIllnessKes: 850000,
      personalAccidentKes: 5000000,
      numberWithSpouses: 0,
    },
  };
}

export function GroupMedicalForm() {
  const api = useApiBase();
  const [proposer, setProposer] = useState({
    icpakMemberNumber: "",
    fullName: "",
    firmName: "",
    phone: "",
    email: "",
  });
  const [criticalIllnessExtension, setCI] = useState(true);
  const [averageAgeBand, setAgeBand] = useState<AgeBand>("31-40");
  const [groupPersonalAccidentExtension, setGPA] = useState(true);
  const [coverStartDate, setStart] = useState(todayIso());
  const [coverBenefitManagement, setCBM] = useState<"SMART" | "MANUAL">("SMART");
  const [alreadyOnSmart, setOnSmart] = useState(false);
  const [coPayments, setCoPay] = useState(false);
  const [categories, setCategories] = useState<JobCategory[]>([emptyCategory("A")]);
  const [reference, setReference] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);



  function addCategory() {
    if (categories.length >= 6) return;
    const next = CAT_IDS[categories.length];
    if (next) setCategories([...categories, emptyCategory(next)]);
  }

  function removeCategory(idx: number) {
    setCategories(categories.filter((_, i) => i !== idx));
  }

  function updateCategory(idx: number, cat: JobCategory) {
    setCategories(categories.map((c, i) => (i === idx ? cat : c)));
  }

  const payload = {
    proposer,
    criticalIllnessExtension,
    averageAgeBand,
    groupPersonalAccidentExtension,
    coverStartDate,
    coverBenefitManagement,
    alreadyOnSmart,
    coPayments,
    categories,
  };
  const { errors, allErrors, isValid, parsed, touch, touchAll } = useFormValidation(
    groupMedicalPayloadSchema,
    payload,
  );
  const quote = isValid && parsed ? priceGroupMedical(parsed, groupMedicalRates) : null;

  const steps: StepDef[] = useMemo(
    () => [
      {
        key: "proposer",
        label: "Proposer Details",
        validate: () => {
          const fields = ["proposer.icpakMemberNumber", "proposer.fullName", "proposer.firmName", "proposer.phone", "proposer.email"];
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) { fields.forEach(touch); return false; }
          return true;
        },
      },
      {
        key: "config",
        label: "Scheme Configuration",
        validate: () => {
          if ("coverStartDate" in allErrors) { touch("coverStartDate"); return false; }
          return true;
        },
      },
      {
        key: "categories",
        label: "Job Categories",
        validate: () => {
          // Ensure at least one category has members
          const hasMember = categories.some((c) =>
            Object.values(c.population).some((v) => v > 0),
          );
          if (!hasMember) return false;
          return true;
        },
      },
      {
        key: "options",
        label: "Benefit Options",
        // dropdowns always valid
      },
      {
        key: "review",
        label: "Review & Submit",
      },
    ],
    [allErrors, touch, categories],
  );

  const wizard = useStepWizard(steps);

  async function onSubmit() {
    if (!isValid) { touchAll(); return; }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submit(api, "group_medical", parsed!);
      setReference(res.reference);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  const propSet = (k: keyof typeof proposer) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setProposer({ ...proposer, [k]: e.target.value });

  // After submission: confirmation
  if (reference) {
    return (
      <div className="fa-bg-green-50 fa-border fa-border-green-300 fa-rounded-md fa-p-4 fa-text-center fa-mt-4">
        <p className="fa-text-green-800 fa-font-semibold">Quote Request Submitted</p>
        <p className="fa-text-green-700 fa-text-sm fa-mt-1">
          Your reference is <b>{reference}</b>.
        </p>
        <p className="fa-text-green-700 fa-text-sm fa-mt-1">
          Our team will contact you at <b>{proposer.email}</b> within one (1) working day
          with a detailed quote and payment instructions.
        </p>
      </div>
    );
  }

  return (
    <div ref={wizard.containerRef}>
      <StepIndicator
        steps={steps}
        currentIndex={wizard.currentStepIndex}
        completedSteps={wizard.completedSteps}
        onStepClick={wizard.goTo}
      />

      {/* Step 1: Proposer Details */}
      {wizard.currentStep.key === "proposer" && (
        <div className="fa-step-enter" key="proposer">
          <div className="fa-section fa-section-orange fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-3">
            <Field id="proposer.icpakMemberNumber" label="ICPAK Member Number of Proposer" error={errors["proposer.icpakMemberNumber"]} required>
              <input
                id="proposer.icpakMemberNumber"
                value={proposer.icpakMemberNumber}
                onChange={propSet("icpakMemberNumber")}
                onBlur={() => touch("proposer.icpakMemberNumber")}
              />
            </Field>
            <Field id="proposer.fullName" label="Full Name of Proposer" error={errors["proposer.fullName"]} required>
              <input
                id="proposer.fullName"
                value={proposer.fullName}
                onChange={propSet("fullName")}
                onBlur={() => touch("proposer.fullName")}
              />
            </Field>
            <Field id="proposer.firmName" label="Full Name of Firm" error={errors["proposer.firmName"]} required>
              <input
                id="proposer.firmName"
                value={proposer.firmName}
                onChange={propSet("firmName")}
                onBlur={() => touch("proposer.firmName")}
              />
            </Field>
            <Field id="proposer.phone" label="Phone Number of Firm/Proposer" error={errors["proposer.phone"]} required>
              <input
                id="proposer.phone"
                value={proposer.phone}
                onChange={propSet("phone")}
                onBlur={() => touch("proposer.phone")}
                placeholder="0712345678 or +254712345678"
              />
            </Field>
            <Field id="proposer.email" label="Email Address of Firm/Proposer" error={errors["proposer.email"]} required>
              <input
                id="proposer.email"
                type="email"
                value={proposer.email}
                onChange={propSet("email")}
                onBlur={() => touch("proposer.email")}
              />
            </Field>
          </div>
        </div>
      )}

      {/* Step 2: Scheme Configuration */}
      {wizard.currentStep.key === "config" && (
        <div className="fa-step-enter" key="config">
          <div className="fa-section fa-section-orange fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-3">
            <Field id="criticalIllnessExtension" label="Include Critical Illness Extension?">
              <select
                id="criticalIllnessExtension"
                value={criticalIllnessExtension ? "YES" : "NO"}
                onChange={(e) => setCI(e.target.value === "YES")}
              >
                <option value="YES">YES</option>
                <option value="NO">NO</option>
              </select>
            </Field>
            <Field id="averageAgeBand" label="Members' Average Age Group (for CI rider)">
              <select
                id="averageAgeBand"
                value={averageAgeBand}
                onChange={(e) => setAgeBand(e.target.value as AgeBand)}
              >
                {(["21-30", "31-40", "41-50", "51-60"] as const).map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="groupPersonalAccidentExtension" label="Include Group Personal Accident Extension?">
              <select
                id="groupPersonalAccidentExtension"
                value={groupPersonalAccidentExtension ? "YES" : "NO"}
                onChange={(e) => setGPA(e.target.value === "YES")}
              >
                <option value="YES">YES</option>
                <option value="NO">NO</option>
              </select>
            </Field>
            <Field id="coverStartDate" label="Cover Start Date" error={errors.coverStartDate} required>
              <input
                id="coverStartDate"
                type="date"
                value={coverStartDate}
                min={todayIso()}
                onChange={(e) => setStart(e.target.value)}
                onBlur={() => touch("coverStartDate")}
              />
            </Field>
          </div>
        </div>
      )}

      {/* Step 3: Job Categories */}
      {wizard.currentStep.key === "categories" && (
        <div className="fa-step-enter" key="categories">
          {categories.map((cat, i) => (
            <CategoryBlock
              key={cat.id}
              cat={cat}
              index={i}
              onChange={(c) => updateCategory(i, c)}
              onRemove={i > 0 ? () => removeCategory(i) : undefined}
            />
          ))}
          {categories.length < 6 && (
            <div className="fa-my-3">
              <button className="fa-btn-orange" onClick={addCategory}>
                Add Another Job Category
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Benefit Options */}
      {wizard.currentStep.key === "options" && (
        <div className="fa-step-enter" key="options">
          <div className="fa-section fa-section-orange fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-3">
            <Field id="coverBenefitManagement" label="Cover Benefit Management">
              <select
                id="coverBenefitManagement"
                value={coverBenefitManagement}
                onChange={(e) => setCBM(e.target.value as "SMART" | "MANUAL")}
              >
                <option value="SMART">Smart Card</option>
                <option value="MANUAL">Manual</option>
              </select>
            </Field>
            {coverBenefitManagement === "SMART" && (
              <Field id="alreadyOnSmart" label="Are you already on Smart? (card fees waived if YES)">
                <select
                  id="alreadyOnSmart"
                  value={alreadyOnSmart ? "YES" : "NO"}
                  onChange={(e) => setOnSmart(e.target.value === "YES")}
                >
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
              </Field>
            )}
            <Field id="coPayments" label="Consider Co-Payments? (10% discount on Net Premium)">
              <select
                id="coPayments"
                value={coPayments ? "YES" : "NO"}
                onChange={(e) => setCoPay(e.target.value === "YES")}
              >
                <option value="NO">NO</option>
                <option value="YES">YES</option>
              </select>
            </Field>
          </div>
        </div>
      )}

      {/* Step 5: Review & Submit */}
      {wizard.currentStep.key === "review" && (
        <div className="fa-step-enter" key="review">
          <ReviewSummary
            sections={[
              {
                title: "Proposer Details",
                fields: [
                  { label: "ICPAK Member Number", value: proposer.icpakMemberNumber },
                  { label: "Full Name", value: proposer.fullName },
                  { label: "Firm Name", value: proposer.firmName },
                  { label: "Phone", value: proposer.phone },
                  { label: "Email", value: proposer.email },
                ],
              },
              {
                title: "Scheme Configuration",
                fields: [
                  { label: "Critical Illness Extension", value: criticalIllnessExtension ? "Yes" : "No" },
                  { label: "Average Age Band", value: averageAgeBand },
                  { label: "Group Personal Accident", value: groupPersonalAccidentExtension ? "Yes" : "No" },
                  { label: "Cover Start Date", value: coverStartDate },
                ],
              },
              ...categories.map((cat) => {
                const summary = summarizeCategory(cat);
                return {
                  title: `Category ${cat.id}`,
                  fields: [
                    { label: "Families", value: String(summary.families) },
                    { label: "Lives", value: String(summary.lives) },
                    { label: "Inpatient Limit", value: `KES ${cat.limits.inpatientKes.toLocaleString()}` },
                    { label: "Outpatient Limit", value: `KES ${cat.limits.outpatientKes.toLocaleString()}` },
                    { label: "Dental Limit", value: `KES ${cat.limits.dentalKes.toLocaleString()}` },
                    { label: "Optical Limit", value: `KES ${cat.limits.opticalKes.toLocaleString()}` },
                    { label: "Maternity Limit", value: `KES ${cat.limits.maternityKes.toLocaleString()}` },
                    { label: "Last Expense", value: `KES ${cat.limits.lastExpenseKes.toLocaleString()}` },
                  ],
                };
              }),
              {
                title: "Benefit Options",
                fields: [
                  { label: "Cover Benefit Management", value: coverBenefitManagement === "SMART" ? "Smart Card" : "Manual" },
                  ...(coverBenefitManagement === "SMART" ? [{ label: "Already on Smart", value: alreadyOnSmart ? "Yes" : "No" }] : []),
                  { label: "Co-Payments", value: coPayments ? "Yes" : "No" },
                ],
              },
            ]}
          />
          {quote && (
            <>
              <RatesDisclaimer />
              <QuoteTable title="Premium Calculation" quote={quote} />
            </>
          )}
          <FormError message={submitError} />
        </div>
      )}

      <StepNavigation
        isFirstStep={wizard.isFirstStep}
        isLastStep={wizard.isLastStep}
        onBack={wizard.goBack}
        onNext={wizard.goNext}
        onSubmit={onSubmit}
        submitLoading={isSubmitting}
        submitDisabled={!isValid}
        submitLabel="Request for Detailed Quote"
      />
    </div>
  );
}

function CategoryBlock({
  cat,
  index,
  onChange,
  onRemove,
}: {
  cat: JobCategory;
  index: number;
  onChange: (c: JobCategory) => void;
  onRemove?: () => void;
}) {
  const summary = summarizeCategory(cat);
  const setPop = (k: keyof JobCategory["population"], v: number) =>
    onChange({ ...cat, population: { ...cat.population, [k]: v } });
  const setLim = (k: keyof JobCategory["limits"], v: number) =>
    onChange({ ...cat, limits: { ...cat.limits, [k]: v } });

  return (
    <div className="fa-border fa-border-slate-200 fa-rounded-md fa-p-4 fa-my-3">
      <div className="fa-flex fa-items-center fa-mb-2">
        <div className="fa-font-semibold">Category {cat.id}</div>
        {onRemove && (
          <button
            className="fa-text-red-600 fa-text-sm fa-underline fa-ml-auto"
            onClick={onRemove}
          >
            Remove Category {cat.id}
          </button>
        )}
      </div>

      <div className="fa-font-semibold fa-text-sm fa-text-slate-500 fa-uppercase fa-tracking-wide fa-mt-3 fa-mb-1">
        Staff Population
      </div>
      <div className="fa-grid fa-grid-cols-2 fa-md:grid-cols-7 fa-gap-2">
        {(["M", "Mplus1", "Mplus2", "Mplus3", "Mplus4", "Mplus5", "additionalDependents"] as const).map(
          (k) => (
            <div key={k}>
              <label>{POP_LABELS[k]}</label>
              <input
                type="number"
                min={0}
                value={cat.population[k]}
                onChange={(e) => setPop(k, Number(e.target.value))}
              />
            </div>
          ),
        )}
      </div>
      <div className="fa-text-xs fa-mt-2">
        Families: <b>{summary.families}</b> | Lives: <b>{summary.lives}</b> |
        Principals+Spouses: <b>{summary.principalsPlusSpouses}</b>
      </div>

      <div className="fa-font-semibold fa-text-sm fa-text-slate-500 fa-uppercase fa-tracking-wide fa-mt-4 fa-mb-1">
        Coverage Limits (KES)
      </div>
      <div className="fa-grid fa-grid-cols-2 fa-md:grid-cols-4 fa-gap-2">
        {(
          [
            ["inpatientKes", "Inpatient Limit"],
            ["outpatientKes", "Outpatient Limit"],
            ["dentalKes", "Dental Limit"],
            ["opticalKes", "Optical Limit"],
            ["maternityKes", "Maternity Limit"],
            ["lastExpenseKes", "Last Expense"],
            ["criticalIllnessKes", "Critical Illness"],
            ["personalAccidentKes", "PA Limit"],
            ["numberWithSpouses", "Principals w/ Spouses"],
          ] as const
        ).map(([k, label]) => (
          <div key={k}>
            <label>{label}</label>
            <input
              type="number"
              min={0}
              value={cat.limits[k]}
              onChange={(e) => setLim(k, Number(e.target.value))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
